import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Mail, CheckCircle } from 'lucide-react';
import StandardNavbar from '../components/StandardNavbar';
import Footer from '../components/Footer';
import { useAuth } from '../context/AuthContext';

function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { login } = useAuth();
  const [isLogin, setIsLogin] = useState(true); // Valor inicial

  // ✅ NUEVO: Sincronizar isLogin con los parámetros de URL
  useEffect(() => {
    const mode = searchParams.get('mode');
    if (mode === 'register') {
      setIsLogin(false);
      setRegistrationStep(1); // Resetear al paso 1 si viene de URL
    } else {
      setIsLogin(true);
    }
  }, [searchParams]);


  // ✅ AGREGAR ESTO - Scroll al top al cargar
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, []);
  
  const [registrationStep, setRegistrationStep] = useState(1); // 1: email, 2: enviado

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    solicitudEmail: ''
  });
  
  const [isLoading, setIsLoading] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // LOGIN (sin cambios)
  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    try {
      const result = await login(formData.email, formData.password);
      
      if (result.success) {
        setMessage({ type: 'success', text: 'Inicio de sesión exitoso' });
        setTimeout(() => {
          if (result.user.role === 'admin') {
            navigate('/admin');
          } else {
            navigate('/');
          }
        }, 1500);
      } else {
        setMessage({ type: 'error', text: result.error });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Error de conexión. Intenta nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  // REGISTRO: Solicitar acceso (solo email)
  const handleSolicitarAcceso = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setMessage({ type: '', text: '' });

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.solicitudEmail)) {
      setMessage({ type: 'error', text: 'Ingresá un email válido' });
      setIsLoading(false);
      return;
    }

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/solicitar-acceso`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: formData.solicitudEmail })
      });

      const data = await response.json();

      if (response.ok) {
        setRegistrationStep(2);
      } else {
        setMessage({ type: 'error', text: data.message || 'Error al enviar la solicitud' });
      }
    } catch (error) {
      console.error('Error:', error);
      setMessage({ type: 'error', text: 'Error de conexión. Intenta nuevamente.' });
    } finally {
      setIsLoading(false);
    }
  };

  const toggleMode = () => {
    setIsLogin(!isLogin);
    setRegistrationStep(1);
    setFormData({ email: '', password: '', solicitudEmail: '' });
    setMessage({ type: '', text: '' });
  };

  return (
    <>
      <Helmet>
        <title>{isLogin ? 'Iniciar Sesión' : 'Registrarse'} | Opciones Argentinas</title>
        <meta name="description" content={isLogin ? 'Inicia sesión en tu cuenta' : 'Crea tu cuenta nueva'} />
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <StandardNavbar />
      
      <div className="relative pt-16 min-h-screen bg-gray-50">
        <div className="container mx-auto px-4 py-12 flex items-center justify-center">
          <div className="max-w-md w-full">
            {/* Logo y título */}
            <div className="text-center mb-8">
              <h1 className="text-3xl font-bold text-[#1E3A5F] mb-2 font-montserrat">
                {isLogin ? 'Iniciar Sesión' : 'Crear Cuenta'}
              </h1>
              <p className="text-gray-600">
                {isLogin 
                  ? 'Accede a tu cuenta para gestionar tus viajes' 
                  : (registrationStep === 1 
                      ? 'Únete a nuestra comunidad de viajeros' 
                      : 'Verifica tu email para completar el registro'
                    )
                }
              </p>
            </div>

            {/* Formulario */}
            <div className="bg-white rounded-lg shadow-lg p-8">

              {message.text && (
                <div className={`mb-6 p-4 rounded-lg ${
                  message.type === 'success' 
                    ? 'bg-green-100 border border-green-400 text-green-700' 
                    : 'bg-red-100 border border-red-400 text-red-700'
                }`}>
                  {message.text}
                </div>
              )}

              {/* LOGIN */}
              {isLogin && (
                <form onSubmit={handleLogin} className="space-y-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      placeholder="Ej: amartinez@mailhost.com"
                      required
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Contraseña *
                    </label>
                    <input
                      type="password"
                      name="password"
                      value={formData.password}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      placeholder="Mínimo 6 caracteres"
                      required
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#2AA8B0] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#1E3A5F] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Procesando...' : 'Iniciar Sesión'}
                  </button>
                </form>
              )}

              {/* REGISTRO - PASO 1: Solicitar acceso */}
              {!isLogin && registrationStep === 1 && (
                <form onSubmit={handleSolicitarAcceso} className="space-y-6">
                  <div className="text-center mb-2">
                    <p className="text-gray-600 text-sm">
                      Ingresá tu email y el equipo de Opciones Argentinas habilitará tu acceso.
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      E-Mail *
                    </label>
                    <input
                      type="email"
                      name="solicitudEmail"
                      value={formData.solicitudEmail}
                      onChange={handleInputChange}
                      className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-[#2AA8B0] focus:border-transparent transition-colors"
                      placeholder="Ej: amartinez@mailhost.com"
                      required
                    />
                  </div>
                  <button
                    type="submit"
                    disabled={isLoading}
                    className="w-full bg-[#2AA8B0] text-white font-bold py-3 px-6 rounded-lg hover:bg-[#1E3A5F] transition-colors duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isLoading ? 'Enviando...' : 'Solicitar Acceso'}
                  </button>
                </form>
              )}

              {/* REGISTRO - PASO 2: Solicitud enviada */}
              {!isLogin && registrationStep === 2 && (
                <div className="text-center space-y-4">
                  <CheckCircle className="w-16 h-16 text-[#2AA8B0] mx-auto" />
                  <h3 className="text-lg font-bold text-[#1E3A5F]">¡Solicitud enviada!</h3>
                  <p className="text-gray-600 text-sm">
                    El equipo de Opciones Argentinas revisará tu solicitud y te enviaremos un email a{' '}
                    <strong>{formData.solicitudEmail}</strong> para habilitar tu acceso.
                  </p>
                  <button
                    type="button"
                    onClick={() => { setIsLogin(true); setRegistrationStep(1); }}
                    className="text-[#2AA8B0] hover:underline text-sm font-medium"
                  >
                    Volver al inicio de sesión
                  </button>
                </div>
              )}

              {/* Toggle entre login y registro */}
              <div className="mt-6 text-center">
                <p className="text-gray-600">
                  {isLogin ? '¿No tienes cuenta?' : '¿Ya tienes cuenta?'}
                  <button
                    type="button"
                    onClick={toggleMode}
                    className="ml-2 text-[#2AA8B0] hover:text-[#1E3A5F] font-medium transition-colors"
                  >
                    {isLogin ? 'Regístrate aquí' : 'Inicia sesión aquí'}
                  </button>
                </p>
              </div>

              {isLogin && (
                <div className="text-center mt-4">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#2AA8B0] hover:text-[#1E8A91] font-medium"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              )}

              {/* ✅ NUEVO: Aviso para usuarios del sitio anterior */}
              <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg">
                <div className="flex items-start gap-3">
                  <span className="text-amber-500 text-xl">⚠️</span>
                  <div>
                    <p className="text-sm text-amber-800 font-medium mb-1">
                      Importante para usuarios anteriores
                    </p>
                    <p className="text-xs text-amber-700">
                      Si tenías una cuenta creada antes del 01/12/2025, deberás volver a registrarte. 
                      Por motivos de seguridad, todas las cuentas anteriores fueron eliminadas.
                    </p>
                  </div>
                </div>
              </div>

              {isLogin && (
                <div className="text-center mt-4">
                  <Link
                    to="/forgot-password"
                    className="text-sm text-[#2AA8B0] hover:text-[#1E8A91] font-medium"
                  >
                    ¿Olvidaste tu contraseña?
                  </Link>
                </div>
              )}
            </div>

            {/* Beneficios de crear cuenta */}
            <div className="mt-8 bg-white rounded-lg shadow-lg p-6">
              <h3 className="text-lg font-bold text-[#1E3A5F] mb-4">
                {isLogin ? 'Beneficios de tener cuenta' : '¿Por qué crear una cuenta?'}
              </h3>
              <ul className="space-y-2 text-gray-600">
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-[#2AA8B0] mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Guarda tus viajes favoritos
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-[#2AA8B0] mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Acceso a ofertas exclusivas
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-[#2AA8B0] mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Historial de reservas
                </li>
                <li className="flex items-center">
                  <svg className="w-5 h-5 text-[#2AA8B0] mr-2" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                  </svg>
                  Notificaciones de nuevos destinos
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
      
      <Footer />
    </>
  );
}

export default Login;